<template>
    <div class="space-y-6">
        <!-- MARKDOWN MODAL -->
        <TabbedMarkdownModal
            :visible="!!(markdownModalVisible && markdownConfig)"
            :config="markdownConfig"
            @save="onSaveMarkdown"
            @update:visible="val => markdownModalVisible = val"
        />
        <div
            v-if="configs.length"
            class="space-y-3"
        >
            <div
                v-for="(config, index) in configs"
                :key="index"
            >
                <div
                    v-if="isPromptsModal(config)"
                >
                    <AiInstructionsBlock
                        :config="config"
                        :error-message="getPromptError(config)"
                        @configure="openMarkdownModal(config)"
                    />
                </div>
                <RedesignWidgetConfigSelectExtended
                    v-else-if="isSelectExtended(config)"
                    :value="config"
                    horizontal
                    :required="isRequiredSelectExtended(config)"
                    :error-message="getSelectExtendedError(config)"
                    class="mb-4"
                    @update:value="onSelectExtendedUpdate"
                />
                <div
                    v-else
                    class="max-w-sm"
                >
                    <RedesignWidgetConfigEntityTree
                        v-if="isAutoComplete(config)"
                        :key="`filter-tree-${config.ParameterID}`"
                        :value="config"
                        class="mb-4"
                        @update:value="emitUpdate"
                    />
                    <RedesignWidgetConfigAutoComplete
                        v-if="isAutoCompleteSelect(config)"
                        :value="config"
                        class="mb-4"
                        @update:value="emitUpdate"
                    />

                    <RedesignWidgetConfigSelect
                        v-else-if="isMultiSelect(config)"
                        :value="config"
                        @update:value="emitUpdate"
                    />

                    <RedesignWidgetOtherFilters
                        v-else-if="isOtherFilters(config.ParameterType || -1)"
                        :value="config"
                        :parameter-type="config.ParameterType"
                        @update:value="emitUpdate"
                    />

                    <RedesignWidgetAiParameter
                        v-if="isSummaryCdrTemplate(config)"
                        :value="config"
                        @update:value="emitUpdate"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import TabbedMarkdownModal
    from '@/components/RedesignComponents/Modal/TabbedMarkdownModal.vue'
import AiInstructionsBlock
    from '@/components/RedesignComponents/Widgets/Common/AiInstructionsBlock.vue'

import RedesignWidgetConfigSelectExtended
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelectExtended.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'
import RedesignWidgetConfigSelect
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetOtherFilters
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import RedesignWidgetAiParameter
    from '@/components/RedesignComponents/Widgets/RedesignWidgetAiParameter.vue'

import {
    isSelectExtended,
    isAutoComplete,
    isMultiSelect,
    isPromptsModal,
    isOtherFilters,
    isSummaryCdrTemplate,
    isAutoCompleteSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { validatePromptsConfig } from '@/composables/usePromptsValidation'

import { WidgetConfig } from '@/types/dashboard'
import i18n from '@/i18n'
import RedesignWidgetConfigAutoComplete
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigAutoComplete.vue'
import { QUESTIONS_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'

const requiredSelectExtendedNames = [ QUESTIONS_PARAMETER_NAME ]

const props = defineProps<{
    configs: WidgetConfig[]
}>()

const emit = defineEmits<{
    (e: 'update:config', value: WidgetConfig): void
}>()

const markdownModalVisible = ref(false)
const markdownConfig = ref<WidgetConfig | null>(null)
const promptErrors = ref<Record<number, string>>({})
const selectExtendedErrors = ref<Record<number, string>>({})

const promptsModalConfigs = computed(() =>
    props.configs.filter(config => isPromptsModal(config))
)

function validatePrompts (): boolean {
    if (!promptsModalConfigs.value.length) {
        return true
    }

    return promptsModalConfigs.value.every(config => validatePromptsConfig(config))
}

function getPromptError (config: WidgetConfig): string {
    const paramId = config.ParameterID
    if (!paramId) return ''
    return promptErrors.value[paramId] || ''
}

function showPromptsError () {
    // Use the same error message pattern as useValidationRules for consistency
    const errorMessage = i18n.t('validation.required') as string

    // Create new object to ensure reactivity
    const newErrors: Record<number, string> = {}

    promptsModalConfigs.value.forEach(config => {
        const paramId = config.ParameterID
        if (paramId !== undefined && paramId !== null) {
            if (!validatePromptsConfig(config)) {
                newErrors[paramId] = errorMessage
            }
        }
    })

    promptErrors.value = newErrors
}

function clearPromptsError () {
    promptErrors.value = {}
}

function onSaveMarkdown (val: WidgetConfig) {
    emitUpdate(val)
    if (validatePromptsConfig(val)) {
        delete promptErrors.value[val.ParameterID || -1]
    }
}

const openMarkdownModal = (config: WidgetConfig) => {
    markdownConfig.value = config
    markdownModalVisible.value = true
}

function emitUpdate (config: WidgetConfig) {
    emit('update:config', config)
}

function isRequiredSelectExtended (config: WidgetConfig): boolean {
    return requiredSelectExtendedNames.includes(config.ParameterName ?? '')
}

function validateSelectExtendedValue (config: WidgetConfig): boolean {
    const value = config.WidgetParameterValue ?? config.WidgetParameterValueJson
    return !!value && String(value).trim().length > 0
}

const requiredSelectExtendedConfigs = computed(() =>
    props.configs.filter(config => isSelectExtended(config) && isRequiredSelectExtended(config))
)

function validateSelectExtended (): boolean {
    if (!requiredSelectExtendedConfigs.value.length) {
        return true
    }
    return requiredSelectExtendedConfigs.value.every(validateSelectExtendedValue)
}

function getSelectExtendedError (config: WidgetConfig): string {
    const paramId = config.ParameterID
    if (!paramId) return ''
    return selectExtendedErrors.value[paramId] || ''
}

function showSelectExtendedError () {
    const errorMessage = i18n.t('validation.required') as string
    const newErrors: Record<number, string> = {}

    requiredSelectExtendedConfigs.value.forEach(config => {
        const paramId = config.ParameterID
        if (paramId !== undefined && paramId !== null) {
            if (!validateSelectExtendedValue(config)) {
                newErrors[paramId] = errorMessage
            }
        }
    })

    selectExtendedErrors.value = newErrors
}

function clearSelectExtendedError () {
    selectExtendedErrors.value = {}
}

function onSelectExtendedUpdate (config: WidgetConfig) {
    if (config.ParameterID && selectExtendedErrors.value[config.ParameterID]) {
        if (validateSelectExtendedValue(config)) {
            const newErrors = { ...selectExtendedErrors.value }
            delete newErrors[config.ParameterID]
            selectExtendedErrors.value = newErrors
        }
    }
    emitUpdate(config)
}

defineExpose({
    validatePrompts,
    showPromptsError,
    clearPromptsError,
    validateSelectExtended,
    showSelectExtendedError,
    clearSelectExtendedError
})
</script>
